/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: AI识别记录页面
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-07-25 11:26:44
 */
import React, { useEffect } from 'react'
import { Layout, Table, Button, Empty, Typography } from 'antd'
import type { TableProps } from 'antd'
import AntdProvider from '@/components/AntdProvider'
import verify_bg from '../../images/verify-auth/verify_bg.jpg'
import { RecognitionRecord } from './types'
import { useExportList } from './hooks/useExportList'
import { useSensorTracking } from './hooks/useSensorTracking'
import { AIRecognitionProvider } from './context/AIRecognitionContext'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

/**
 * AI识别记录页面组件内容
 * 参考照片台账表页面样式，但删除筛选功能
 */
const AIRecognitionExportListContent: React.FC = React.memo(() => {
  // 使用自定义hook管理列表状态和事件
  const {
    recognitionRecords,
    currentPage,
    currentPageSize,
    totalRecords,
    loading,
    handlePageChange,
    handlePageSizeChange,
    handleDownloadExcel
  } = useExportList()

  // 使用埋点hook
  const { reportAISafetyHazardsHistoryClick } = useSensorTracking()

  // 页面进入埋点
  useEffect(() => {
    reportAISafetyHazardsHistoryClick({
      clickItem: 'enterPage'
    })
  }, [reportAISafetyHazardsHistoryClick])

  // 定义表格列
  const columns: TableProps<RecognitionRecord>['columns'] = [
    {
      title: '编号',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 40,
      render: (text: string, record: RecognitionRecord) => text || record.id
    },
    {
      title: '照片/视频',
      dataIndex: 'photoUrl',
      key: 'photoUrl',
      width: 80,
      render: (text: string, record: RecognitionRecord) => (
        <div
          style={{
            width: 68,
            height: 68,
            backgroundColor: '#D9D9D9',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: '#666'
          }}
        >
          {record.url ? (
            <img
              src={record.url}
              alt={record.url}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 4
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
              }}
            />
          ) : (
            '暂无图片'
          )}
        </div>
      )
    },
    {
      title: '拍摄人',
      dataIndex: 'photographer',
      key: 'photographer',
      width: 120,
      render: (text: string, record: RecognitionRecord) => (
        <div title={text || record.nickName || '-'}>
          <Typography.Paragraph
            ellipsis={{ rows: 2 }}
            style={{ maxWidth: 120, margin: 0 }}
          >
            {text || record.nickName || '-'}
          </Typography.Paragraph>
        </div>
      )
    },
    {
      title: '识别到的隐患',
      dataIndex: 'detectedHazards',
      key: 'detectedHazards',
      width: 300,
      render: (text: string, record: RecognitionRecord) => {
        let hazardList: string[] = []

        if (text) {
          hazardList = [text]
        } else if (Array.isArray(record.potentialHazardList)) {
          hazardList = record.potentialHazardList
        } else if (record.potentialHazardList) {
          hazardList = [record.potentialHazardList]
        }

        if (hazardList.length === 0) {
          return <span>-</span>
        }

        const formattedContent =
          hazardList.length > 1
            ? hazardList
                .map((hazard, index) => `${index + 1}. ${hazard}`)
                .join('\n')
            : hazardList[0]

        return (
          <Typography.Paragraph
            ellipsis={{ rows: 3, expandable: true }}
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {formattedContent}
          </Typography.Paragraph>
        )
      }
    },
    {
      title: '参考标准',
      dataIndex: 'referenceStandard',
      key: 'referenceStandard',
      width: 500,
      render: (text: string, record: RecognitionRecord) => {
        let standardList: string[] = []

        if (text) {
          standardList = [text]
        } else if (Array.isArray(record.standardList)) {
          standardList = record.standardList
        } else if (record.standardList) {
          standardList = [record.standardList]
        }

        if (standardList.length === 0) {
          return <span>-</span>
        }

        const formattedContent = standardList
          .map((standard) => standard)
          .join('\n')

        return (
          <Typography.Paragraph
            ellipsis={{ rows: 3, expandable: true }}
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {formattedContent}
          </Typography.Paragraph>
        )
      }
    },
    {
      title: '思考过程',
      dataIndex: 'thinkingProcess',
      key: 'thinkingProcess',
      width: 300,
      render: (text: string, record: RecognitionRecord) => (
        <div title={text || record.thinking || '-'}>
          <Typography.Paragraph ellipsis={{ rows: 3, expandable: true }}>
            {text || record.thinking || '-'}
          </Typography.Paragraph>
        </div>
      )
    },
    {
      title: '识别时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (text: string, record: RecognitionRecord) => (
        <div title={text || record.createTime || '-'}>
          <Typography.Paragraph ellipsis={{ rows: 3, expandable: true }}>
            {text || record.createTime || '-'}
          </Typography.Paragraph>
        </div>
      )
    }
  ]

  return (
    <Layout className="ai-recognition-export-list">
      <PageHeader
        ghost={false}
        title="识别记录"
        extra={
          [
            <Button
              key="export"
              type="primary"
              onClick={handleDownloadExcel}
              loading={loading}
            >
              下载表格Excel
            </Button>
          ] as any
        }
      />
      <Content
        style={{
          backgroundImage: `url(${verify_bg})`,
          backgroundSize: 'cover'
        }}
      >
        {recognitionRecords.length ? (
          <div className="table-wrapper">
            <Table
              columns={columns}
              dataSource={recognitionRecords}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                total: totalRecords,
                pageSize: currentPageSize,
                onChange: handlePageChange,
                onShowSizeChange: handlePageSizeChange,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条/共 ${total} 条`
              }}
              scroll={{ x: 1200, y: 600 }}
              size="middle"
              style={{
                backgroundColor: '#fff',
                borderRadius: 8,
                boxShadow: '-2px -1px 13.6px 0px rgba(233, 233, 233, 1)',
                maxHeight: 100,
                marginLeft: 22,
                marginRight: 22,
                marginTop: 44
              }}
            />
          </div>
        ) : (
          <Empty style={{ marginTop: '200px' }} description="暂无识别记录" />
        )}
      </Content>
    </Layout>
  )
})

/**
 * AI识别记录页面主组件
 */
const AIRecognitionExportList: React.FC = React.memo(() => {
  return (
    <AntdProvider>
      <AIRecognitionProvider>
        <AIRecognitionExportListContent />
      </AIRecognitionProvider>
    </AntdProvider>
  )
})

export default AIRecognitionExportList
